'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Plus, ChevronLeft, ChevronRight, Calendar, List, Clock, User, Trash2, Edit2, X, Check, Bell, BellOff } from 'lucide-react';
import AppLayout from '@/components/AppLayout';
import { AgendaEvent } from '@/lib/familyDemoData';
import { useFamilyDemo } from '@/lib/familyDemoState';

type AgendaView = 'list' | 'calendar';

const EVENT_TYPES: { value: AgendaEvent['type']; label: string; color: string; bg: string }[] = [
  { value: 'visit', label: 'Visite', color: 'text-lucat-green', bg: 'bg-lucat-green-light' },
  { value: 'call', label: 'Appel', color: 'text-lucat-blue', bg: 'bg-lucat-blue-light' },
  { value: 'family', label: 'Événement famille', color: 'text-lucat-purple', bg: 'bg-lucat-purple-light' },
  { value: 'activity', label: 'Activité', color: 'text-lucat-teal', bg: 'bg-lucat-teal-light' },
  { value: 'reminder', label: 'Rappel familial', color: 'text-lucat-orange', bg: 'bg-lucat-orange-light' },
];

const CONTACTS_LIST = [
  { id: 'marie', label: 'Marie (moi)' },
  { id: 'jean', label: 'Jean' },
  { id: 'sophie', label: 'Sophie' },
  { id: 'paul', label: 'Paul' },
  { id: 'claire', label: 'Claire' },
  { id: null, label: 'Toute la famille' },
];

function getEventTypeInfo(type: AgendaEvent['type']) {
  return EVENT_TYPES.find((t) => t.value === type) ?? EVENT_TYPES[0];
}

function formatIsoToDisplay(isoDate: string): string {
  try {
    const d = new Date(isoDate + 'T00:00:00');
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const target = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const diff = Math.round((target.getTime() - today.getTime()) / 86400000);
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Demain';
    if (diff === -1) return 'Hier';
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    if (diff > 1 && diff < 7) return days[d.getDay()];
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  } catch { return isoDate; }
}

function getTodayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

interface EventFormData {
  title: string;
  isoDate: string;
  time: string;
  type: AgendaEvent['type'];
  contactId: string | null;
  note: string;
  reminder: boolean;
}

const EMPTY_FORM: EventFormData = {
  title: '',
  isoDate: getTodayIso(),
  time: '10:00',
  type: 'visit',
  contactId: 'marie',
  note: '',
  reminder: false,
};

export default function AgendaClient() {
  const router = useRouter();
  const [view, setView] = useState<AgendaView>('list');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<EventFormData>(EMPTY_FORM);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const { state, addAgendaEvent, updateAgendaEvent, deleteAgendaEvent, addActivity } = useFamilyDemo();
  const unreadAlerts = 0; // AppLayout reads from provider directly
  const unreadMessages = 0;

  // Sort events: those with isoDate first, then by time
  const sortedEvents = [...state.agendaEvents].sort((a, b) => {
    const aIso = a.isoDate ?? '';
    const bIso = b.isoDate ?? '';
    if (aIso && bIso) return aIso.localeCompare(bIso) || a.time.localeCompare(b.time);
    if (aIso) return -1;
    if (bIso) return 1;
    return 0;
  });

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, isoDate: getTodayIso() });
    setShowForm(true);
  };

  const openEdit = (evt: AgendaEvent) => {
    setEditingId(evt.id);
    setForm({
      title: evt.title ?? evt.label ?? '',
      isoDate: evt.isoDate ?? getTodayIso(),
      time: evt.time.replace('h', ':').padEnd(5, '0'),
      type: evt.type,
      contactId: evt.contactId ?? evt.concernedPersonId ?? null,
      note: evt.note ?? '',
      reminder: evt.reminderEnabled ?? evt.reminder ?? false,
    });
    setShowForm(true);
  };

  const handleSave = () => {
    if (!form.title.trim()) return;
    const [h, m] = form.time.split(':');
    const timeLabel = `${h}h${m ?? '00'}`;
    const displayDate = formatIsoToDisplay(form.isoDate);
    const typeInfo = getEventTypeInfo(form.type);

    const colorMap: Record<string, string> = {
      visit: 'lucat-green', call: 'lucat-blue', family: 'lucat-purple',
      activity: 'lucat-teal', reminder: 'lucat-orange',
    };

    if (editingId) {
      updateAgendaEvent(editingId, {
        title: form.title,
        label: form.title,
        isoDate: form.isoDate,
        date: displayDate,
        time: timeLabel,
        type: form.type,
        contactId: form.contactId,
        concernedPersonId: form.contactId,
        note: form.note || undefined,
        reminderEnabled: form.reminder,
        reminder: form.reminder,
        color: colorMap[typeInfo.value] ?? 'lucat-green',
      });
      addActivity({
        id: `act-agenda-edit-${Date.now()}`,
        seniorId: 'andre',
        actorId: 'marie',
        type: 'agenda',
        title: `Événement modifié : ${form.title} — ${displayDate} à ${timeLabel}`,
        createdAt: new Date().toISOString(),
        status: 'informational',
        // Legacy compat
        category: 'agenda',
        action: 'agenda_edited',
        label: `Événement modifié : ${form.title} — ${displayDate} à ${timeLabel}`,
        contactId: form.contactId ?? undefined,
      });
    } else {
      const newEvt: AgendaEvent = {
        id: `evt-${Date.now()}`,
        title: form.title,
        type: form.type,
        concernedPersonId: form.contactId,
        date: displayDate,
        time: timeLabel,
        note: form.note || undefined,
        reminderEnabled: form.reminder,
        status: 'upcoming',
        createdBy: 'marie',
        // Legacy compat
        label: form.title,
        contactId: form.contactId,
        seniorId: 'andre',
        color: colorMap[typeInfo.value] ?? 'lucat-green',
        isoDate: form.isoDate,
        reminder: form.reminder,
      };
      addAgendaEvent(newEvt);
      addActivity({
        id: `act-agenda-${Date.now()}`,
        seniorId: 'andre',
        actorId: 'marie',
        type: 'agenda',
        title: `Nouvel événement : ${form.title} — ${displayDate} à ${timeLabel}`,
        createdAt: new Date().toISOString(),
        status: 'informational',
        // Legacy compat
        category: 'agenda',
        action: 'agenda_created',
        label: `Nouvel événement : ${form.title} — ${displayDate} à ${timeLabel}`,
        contactId: form.contactId ?? undefined,
      });
    }
    setShowForm(false);
    setEditingId(null);
    setTick((t) => t + 1);
  };

  const handleDelete = (id: string) => {
    deleteAgendaEvent(id);
    setDeleteConfirm(null);
    setTick((t) => t + 1);
  };

  // Calendar helpers
  const daysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year: number, month: number) => {
    const d = new Date(year, month, 1).getDay();
    return d === 0 ? 6 : d - 1; // Monday-first
  };
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const dayNames = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const eventsOnDay = (isoDay: string) => sortedEvents.filter((e) => e.isoDate === isoDay);
  const todayIso = getTodayIso();

  const selectedDayEvents = selectedDay ? eventsOnDay(selectedDay) : [];

  return (
    <AppLayout unreadAlerts={unreadAlerts} unreadMessages={unreadMessages}>
      <div className="pb-4">
        {/* Header */}
        <div className="bg-lucat-navy px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-white text-2xl font-bold">Agenda</h1>
              <p className="text-white/60 text-sm mt-0.5">Événements et visites</p>
            </div>
            <button
              onClick={openCreate}
              className="flex items-center gap-1.5 bg-lucat-green text-white rounded-xl px-3 py-2 text-sm font-bold tap-target active:scale-95 transition-all"
            >
              <Plus size={16} />
              Ajouter
            </button>
          </div>
          {/* View toggle */}
          <div className="flex mt-3 bg-white/10 rounded-xl p-1 gap-1">
            <button
              onClick={() => setView('list')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'list' ? 'bg-white text-lucat-navy' : 'text-white/70'}`}
            >
              <List size={15} />
              Liste
            </button>
            <button
              onClick={() => setView('calendar')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all ${view === 'calendar' ? 'bg-white text-lucat-navy' : 'text-white/70'}`}
            >
              <Calendar size={15} />
              Calendrier
            </button>
          </div>
        </div>

        {/* LIST VIEW */}
        {view === 'list' && (
          <div className="px-4 pt-4 flex flex-col gap-3">
            {sortedEvents.length === 0 && (
              <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
                <div className="w-16 h-16 rounded-full bg-lucat-blue-light flex items-center justify-center">
                  <Calendar size={28} className="text-lucat-blue" />
                </div>
                <p className="text-muted-foreground text-sm">Aucun événement.<br />Ajoutez votre première visite !</p>
              </div>
            )}
            {sortedEvents.map((evt) => {
              const typeInfo = getEventTypeInfo(evt.type);
              return (
                <div key={evt.id} className="bg-card rounded-2xl p-4 shadow-card border border-border">
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl ${typeInfo.bg} flex items-center justify-center flex-shrink-0`}>
                      <Calendar size={18} className={typeInfo.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-lucat-navy font-bold text-[15px] leading-tight">{evt.title ?? evt.label}</p>
                        <div className="flex gap-1 flex-shrink-0">
                          <button onClick={() => openEdit(evt)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-muted tap-target active:scale-95 transition-all">
                            <Edit2 size={14} className="text-muted-foreground" />
                          </button>
                          <button onClick={() => setDeleteConfirm(evt.id)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-muted tap-target active:scale-95 transition-all">
                            <Trash2 size={14} className="text-lucat-red" />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                        <div className="flex items-center gap-1">
                          <Clock size={12} className="text-muted-foreground" />
                          <span className="text-muted-foreground text-xs">{evt.date} à {evt.time}</span>
                        </div>
                        {evt.contactId && (
                          <div className="flex items-center gap-1">
                            <User size={12} className="text-muted-foreground" />
                            <span className="text-muted-foreground text-xs capitalize">{evt.contactId}</span>
                          </div>
                        )}
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${typeInfo.bg} ${typeInfo.color}`}>{typeInfo.label}</span>
                      </div>
                      {evt.note && <p className="text-muted-foreground text-xs mt-1.5 italic">{evt.note}</p>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* CALENDAR VIEW */}
        {view === 'calendar' && (
          <div className="px-4 pt-4">
            <div className="bg-card rounded-2xl shadow-card border border-border overflow-hidden">
              {/* Month navigation */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <button onClick={() => setCalendarMonth((p) => { const d = new Date(p.year, p.month - 1); return { year: d.getFullYear(), month: d.getMonth() }; })} className="w-9 h-9 flex items-center justify-center rounded-lg bg-muted tap-target active:scale-95 transition-all">
                  <ChevronLeft size={18} className="text-lucat-navy" />
                </button>
                <p className="text-lucat-navy font-bold text-base">{monthNames[calendarMonth.month]} {calendarMonth.year}</p>
                <button onClick={() => setCalendarMonth((p) => { const d = new Date(p.year, p.month + 1); return { year: d.getFullYear(), month: d.getMonth() }; })} className="w-9 h-9 flex items-center justify-center rounded-lg bg-muted tap-target active:scale-95 transition-all">
                  <ChevronRight size={18} className="text-lucat-navy" />
                </button>
              </div>
              {/* Day headers */}
              <div className="grid grid-cols-7 px-2 pt-2">
                {dayNames.map((d) => (
                  <div key={d} className="text-center text-[11px] font-semibold text-muted-foreground py-1">{d}</div>
                ))}
              </div>
              {/* Days grid */}
              <div className="grid grid-cols-7 px-2 pb-3 gap-y-1">
                {Array.from({ length: firstDayOfMonth(calendarMonth.year, calendarMonth.month) }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                {Array.from({ length: daysInMonth(calendarMonth.year, calendarMonth.month) }).map((_, i) => {
                  const day = i + 1;
                  const isoDay = `${calendarMonth.year}-${String(calendarMonth.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                  const hasEvents = eventsOnDay(isoDay).length > 0;
                  const isToday = isoDay === todayIso;
                  const isSelected = isoDay === selectedDay;
                  return (
                    <button
                      key={day}
                      onClick={() => setSelectedDay(isSelected ? null : isoDay)}
                      className={`relative flex flex-col items-center justify-center h-9 rounded-xl text-sm font-semibold transition-all tap-target active:scale-95
                        ${isSelected ? 'bg-lucat-navy text-white' : isToday ? 'bg-lucat-green text-white' : 'text-foreground hover:bg-muted'}
                      `}
                    >
                      {day}
                      {hasEvents && !isSelected && !isToday && (
                        <span className="absolute bottom-1 w-1 h-1 bg-lucat-green rounded-full" />
                      )}
                      {hasEvents && (isSelected || isToday) && (
                        <span className="absolute bottom-1 w-1 h-1 bg-white rounded-full" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Selected day events */}
            {selectedDay && (
              <div className="mt-4 flex flex-col gap-2">
                <p className="text-lucat-navy font-bold text-sm px-1">{formatIsoToDisplay(selectedDay)}</p>
                {selectedDayEvents.length === 0 && (
                  <div className="bg-card rounded-2xl p-4 shadow-card border border-border text-center">
                    <p className="text-muted-foreground text-sm">Aucun événement ce jour.</p>
                    <button onClick={openCreate} className="mt-2 text-lucat-blue text-sm font-semibold tap-target">Ajouter un événement</button>
                  </div>
                )}
                {selectedDayEvents.map((evt) => {
                  const typeInfo = getEventTypeInfo(evt.type);
                  return (
                    <div key={evt.id} className="bg-card rounded-2xl p-3 shadow-card border border-border flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-xl ${typeInfo.bg} flex items-center justify-center flex-shrink-0`}>
                        <Calendar size={16} className={typeInfo.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-lucat-navy font-bold text-sm">{evt.title ?? evt.label}</p>
                        <p className="text-muted-foreground text-xs">{evt.time} · {typeInfo.label}</p>
                      </div>
                      <button onClick={() => openEdit(evt)} className="w-8 h-8 flex items-center justify-center rounded-lg bg-muted tap-target active:scale-95 transition-all">
                        <Edit2 size={14} className="text-muted-foreground" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 px-4 pb-safe-bottom">
          <div className="bg-card rounded-2xl p-5 w-full max-w-sm shadow-xl mb-4">
            <p className="text-lucat-navy font-bold text-base text-center">Supprimer cet événement ?</p>
            <p className="text-muted-foreground text-sm text-center mt-1">Cette action est irréversible.</p>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button onClick={() => setDeleteConfirm(null)} className="bg-muted text-foreground rounded-xl py-3 text-sm font-semibold tap-target active:scale-95 transition-all">Annuler</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="bg-lucat-red text-white rounded-xl py-3 text-sm font-bold tap-target active:scale-95 transition-all">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Event form modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
          <div className="bg-card rounded-t-3xl w-full max-w-lg shadow-xl overflow-y-auto" style={{ maxHeight: '90vh' }}>
            <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b border-border">
              <h2 className="text-lucat-navy font-bold text-lg">{editingId ? 'Modifier l\'événement' : 'Nouvel événement'}</h2>
              <button onClick={() => setShowForm(false)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-muted tap-target active:scale-95 transition-all">
                <X size={18} className="text-muted-foreground" />
              </button>
            </div>
            <div className="px-5 py-4 flex flex-col gap-4">
              {/* Title */}
              <div>
                <label className="text-lucat-navy text-sm font-semibold mb-1.5 block">Titre *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="Ex : Visite de Marie"
                  className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-lucat-green/40"
                />
              </div>
              {/* Date & Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-lucat-navy text-sm font-semibold mb-1.5 block">Date</label>
                  <input
                    type="date"
                    value={form.isoDate}
                    onChange={(e) => setForm((f) => ({ ...f, isoDate: e.target.value }))}
                    className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-lucat-green/40"
                  />
                </div>
                <div>
                  <label className="text-lucat-navy text-sm font-semibold mb-1.5 block">Heure</label>
                  <input
                    type="time"
                    value={form.time}
                    onChange={(e) => setForm((f) => ({ ...f, time: e.target.value }))}
                    className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-lucat-green/40"
                  />
                </div>
              </div>
              {/* Type */}
              <div>
                <label className="text-lucat-navy text-sm font-semibold mb-1.5 block">Type</label>
                <div className="flex flex-wrap gap-2">
                  {EVENT_TYPES.map((t) => (
                    <button
                      key={t.value}
                      onClick={() => setForm((f) => ({ ...f, type: t.value }))}
                      className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all tap-target active:scale-95 border ${form.type === t.value ? `${t.bg} ${t.color} border-current` : 'bg-muted text-muted-foreground border-transparent'}`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              {/* Contact */}
              <div>
                <label className="text-lucat-navy text-sm font-semibold mb-1.5 block">Personne concernée</label>
                <select
                  value={form.contactId ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, contactId: e.target.value || null }))}
                  className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm text-foreground outline-none focus:ring-2 focus:ring-lucat-green/40"
                >
                  {CONTACTS_LIST.map((c) => (
                    <option key={c.id ?? 'all'} value={c.id ?? ''}>{c.label}</option>
                  ))}
                </select>
              </div>
              {/* Note */}
              <div>
                <label className="text-lucat-navy text-sm font-semibold mb-1.5 block">Note (facultative)</label>
                <textarea
                  value={form.note}
                  onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  placeholder="Informations complémentaires…"
                  rows={2}
                  className="w-full bg-muted rounded-xl px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-2 focus:ring-lucat-green/40 resize-none"
                />
              </div>
              {/* Reminder toggle */}
              <div className="flex items-center justify-between bg-muted rounded-xl px-4 py-3">
                <div className="flex items-center gap-2">
                  {form.reminder ? <Bell size={16} className="text-lucat-green" /> : <BellOff size={16} className="text-muted-foreground" />}
                  <span className="text-sm font-semibold text-foreground">Rappel activé</span>
                </div>
                <button
                  onClick={() => setForm((f) => ({ ...f, reminder: !f.reminder }))}
                  className={`w-12 h-6 rounded-full transition-all relative ${form.reminder ? 'bg-lucat-green' : 'bg-border'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${form.reminder ? 'left-6' : 'left-0.5'}`} />
                </button>
              </div>
              {/* Save */}
              <button
                onClick={handleSave}
                disabled={!form.title.trim()}
                className={`w-full py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 tap-target active:scale-95 transition-all ${form.title.trim() ? 'bg-lucat-green text-white' : 'bg-muted text-muted-foreground'}`}
              >
                <Check size={16} />
                {editingId ? 'Enregistrer les modifications' : 'Ajouter à l\'agenda'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
